import { onMounted, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  fillCompany,
  fillPerson,
  readTokenDonor,
  shouldPrefillFromToken,
  type PrefillableCompany,
  type PrefillablePerson,
  type ResultDonor,
} from '~/libs/resultDonor'

/**
 * A second ask used to prefill the next form through personal URL params. It now sends only the
 * `prefill=1` marker, and the form reads the donor from the result-token cookie the previous form's
 * server route set. Only with the marker (never on an ordinary visit), and only when the URL carries
 * no personal params of its own. Browser-only, once mounted — never into the SSR payload.
 */
function usePrefillFromToken<T extends { address: { countryCode?: string } }>(
  target: Ref<T>,
  fill: (target: T, donor: ResultDonor, countryAtStart: string | undefined) => void,
) {
  const route = useRoute()
  onMounted(async () => {
    if (!shouldPrefillFromToken(route.query)) return
    // What the form held before the (async) read, so a country picked meanwhile is kept.
    const countryAtStart = target.value.address.countryCode
    fill(target.value, await readTokenDonor(), countryAtStart)
  })
}

export function usePrefillPerson(person: Ref<PrefillablePerson>) {
  usePrefillFromToken(person, fillPerson)
}

export function usePrefillCompany(company: Ref<PrefillableCompany>) {
  usePrefillFromToken(company, fillCompany)
}
